import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Clipboard, FileText, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

const NotificationBell = ({ className = '' }) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const initializedRef = useRef(false);
  const previousUnreadRef = useRef(0);
  const audioContextRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    try {
      const AudioContext =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContext) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      const ctx = audioContextRef.current;

      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const now = ctx.currentTime;

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now);
      oscillator.frequency.setValueAtTime(1174, now + 0.10);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(now);
      oscillator.stop(now + 0.38);
    } catch (error) {
      // El sonido nunca debe interrumpir las notificaciones.
      console.debug('No se pudo reproducir sonido:', error);
    }
  }, []);

  const unlockAudio = useCallback(() => {
    try {
      const AudioContext =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContext) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    } catch (error) {
      console.debug('No se pudo inicializar audio:', error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const [listResponse, countResponse] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`)
      ]);

      const nextNotifications = listResponse.data || [];
      const nextUnreadCount = countResponse.data?.count || 0;

      if (
        initializedRef.current &&
        nextUnreadCount > previousUnreadRef.current
      ) {
        playNotificationSound();
      }

      previousUnreadRef.current = nextUnreadCount;
      initializedRef.current = true;

      setNotifications(nextNotifications);
      setUnreadCount(nextUnreadCount);
    } catch (error) {
      // Si el backend todavía no está reiniciado, no romper la interfaz.
      console.debug('No se pudieron cargar notificaciones:', error);
    }
  }, [playNotificationSound]);

  useEffect(() => {
    loadNotifications();

    const interval = setInterval(loadNotifications, 15000);

    return () => clearInterval(interval);
  }, [loadNotifications]);

  const markAsRead = async (notification) => {
    unlockAudio();

    try {
      if (!notification.read) {
        await axios.put(
          `${API}/notifications/${notification.id}/read`
        );

        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, read: true }
              : item
          )
        );

        setUnreadCount((current) => Math.max(current - 1, 0));
      }
    } catch (error) {
      console.debug('No se pudo marcar la notificación:', error);
    }

    setOpen(false);

    if (notification.entity_type === 'daily_report' && notification.entity_id) {
      navigate(`/daily-reports?open=${notification.entity_id}`);
      return;
    }

    if (notification.entity_type === 'work_report' && notification.entity_id) {
      navigate(`/work-reports?open=${notification.entity_id}`);
      return;
    }
  };

  const markAllAsRead = async () => {
    unlockAudio();

    try {
      await axios.put(`${API}/notifications/read-all`);

      setNotifications((current) =>
        current.map((item) => ({
          ...item,
          read: true
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.debug('No se pudieron marcar todas:', error);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';

    try {
      return new Date(value).toLocaleString('es-BO', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const getIcon = (notification) => {
    if (notification.type === 'daily_report_created') {
      return <FileText size={18} />;
    }

    if (notification.type === 'work_report_created') {
      return <Clipboard size={18} />;
    }

    return <Bell size={18} />;
  };

  const getIconClass = (notification) => {
    if (notification.type === 'daily_report_created') {
      return 'bg-orange-100 text-orange-600';
    }

    if (notification.type === 'work_report_created') {
      return 'bg-blue-100 text-blue-600';
    }

    return 'bg-slate-100 text-slate-600';
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => {
          unlockAudio();
          setOpen((value) => !value);
        }}
        className="relative flex items-center justify-center w-11 h-11 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors"
        aria-label="Notificaciones"
      >
        <Bell size={21} />

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-800">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Cerrar notificaciones"
            className="fixed inset-0 z-[70] cursor-default"
            onClick={() => setOpen(false)}
          />

          <div className="fixed md:absolute right-2 md:right-0 top-[70px] md:top-12 z-[80] w-[calc(100vw-16px)] sm:w-[380px] max-w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-800">
                  Notificaciones
                </h3>
                <p className="text-xs text-slate-500">
                  {unreadCount > 0
                    ? `${unreadCount} sin leer`
                    : 'Todo leído'}
                </p>
              </div>

              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllAsRead}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                    title="Marcar todas como leídas"
                  >
                    <CheckCheck size={18} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                  title="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="max-h-[65vh] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  Cargando...
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell
                    size={30}
                    className="mx-auto text-slate-300 mb-2"
                  />
                  <p className="text-sm font-medium text-slate-600">
                    No tienes notificaciones
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Aquí aparecerán los nuevos Partes y Reportes.
                  </p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => markAsRead(notification)}
                    className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-orange-50/60' : 'bg-white'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${getIconClass(
                        notification
                      )}`}
                    >
                      {getIcon(notification)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <p className="text-sm font-semibold text-slate-800 flex-1">
                          {notification.title}
                        </p>

                        {!notification.read && (
                          <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                        )}
                      </div>

                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                        {notification.message}
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
